import React, { useRef, useState, useCallback, useLayoutEffect, useEffect, useMemo } from "react";
import * as d3 from "d3";

import { InlineLoading } from "../../../../components/shared";
import { truncateLabel, charsFor, motionDuration, MOTION_MS, MIN_LABEL_FONT_PX } from "../../../../utils/geneCodes/wheelUtils";
import "./interventionsChart.css";

// Design-system §2 "Categorical Viz Ramp" — identity, not severity. Assigned by index, in order.
const CATEGORICAL_FILLS = Array.from({ length: 16 }, (_, index) => `var(--mg-viz-cat-${index + 1})`);
const fillFor = (index: number): string => CATEGORICAL_FILLS[index % CATEGORICAL_FILLS.length];

const MIN_CHART_SIZE = 220;
const MAX_CHART_SIZE = 760;
const MIN_RADIUS = 40;
const MAX_LOADING_HEIGHT = 260;
const INNER_RATIO = 0.55;

// Fractions of the box width: edge padding, the external label's own column, and the leader-line run.
const EDGE_PAD = 0.02;
const TEXT_ROOM = 0.2;
const TEXT_ROOM_MIN = 40;
const TEXT_ROOM_MAX = 94;
const LEADER_GAP = 0.038;

// Per LINE — a tag wraps to two, which shows every intervention name in full at a desktop width.
const MAX_LABEL_CHARS = 16;
const TAG_MAX_LINES = 2;
const TAG_LINE_PX = 12;
const TAG_PAD = 6;
const TAG_GAP = 10;

// How tall the box may grow to hold a crowded label stack before labels are dropped instead.
const MAX_ASPECT = 1.6;

const LABEL_NUDGE = 6;
const LABEL_TEXT_PAD = 6;

// charsFor is the shared glyph metric; inverted here rather than re-typing its ratio.
const GLYPH_PX = 1000 / charsFor(MIN_LABEL_FONT_PX, 1000);

/** Greedy word wrap to a character budget; the last line is truncated when the words run out of room. */
const wrapLabel = (name: string, chars: number, maxLines: number): string[] => {
	if (chars < 3) return [];

	const lines: string[] = [];
	name.split(/\s+/).forEach((word) => {
		const current = lines[lines.length - 1];
		if (current !== undefined && `${current} ${word}`.length <= chars) lines[lines.length - 1] = `${current} ${word}`;
		else lines.push(word);
	});

	if (lines.length <= maxLines) return lines.map((line) => truncateLabel(line, chars));
	return [...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(" ")].map((line) => truncateLabel(line, chars));
};

const tagFor = (name: string, chars: number): string[] => wrapLabel(name, chars, TAG_MAX_LINES);

/** The shortest wrap that tells the names apart — two genuinely identical names cap the ceiling. */
const distinctLabelChars = (items: ChartDataItem[], from: number): number => {
	const ceiling = new Set(items.map((item) => item.intervention)).size;
	for (let chars = from; chars < MAX_LABEL_CHARS; chars += 1) {
		if (new Set(items.map((item) => tagFor(item.intervention, chars).join(" "))).size >= ceiling) return chars;
	}
	return MAX_LABEL_CHARS;
};
const LEADER_OPACITY = 0.7;

const CENTRE_FONT_PX = 12;
const CENTRE_LINE_PX = 15;
const CENTRE_META_LINE_PX = 13;
const CENTRE_TITLE_LINES = 2;


interface LabelPosition {
	x: number;
	y: number;
	anchor: string;
	lineEndX: number;
	lineEndY: number;
	midX: number;
	midY: number;
	right: boolean;
	idealY: number;
	visible: boolean;
}

/**
 * Places the external labels as two vertical stacks, one per side.
 * A single Y-only nudge cannot separate a crowded side — this walks each stack down and then
 * back up, which converges, and drops the weakest labels outright when the side cannot hold them.
 */
const placeLabels = (arcs: d3.PieArcDatum<ChartDataItem>[], radius: number, leaderGap: number, availHalf: number, suppressed: Set<number>, block: number, step: number): { positions: LabelPosition[]; stackHalf: number } => {
	const anchorRadius = radius + leaderGap;

	const positions: LabelPosition[] = arcs.map((slice, index) => {
		const angle = (slice.startAngle + slice.endAngle) / 2;
		const cos = Math.cos(angle - Math.PI / 2);
		const sin = Math.sin(angle - Math.PI / 2);
		const right = cos >= 0;
		// The anchor x is pinned per side; only y is stacked, so a label never drifts over the ring.
		return {
			x: right ? anchorRadius + LABEL_NUDGE : -anchorRadius - LABEL_NUDGE,
			y: sin * anchorRadius,
			anchor: right ? "start" : "end",
			lineEndX: cos * (radius + leaderGap * 0.15),
			lineEndY: sin * (radius + leaderGap * 0.15),
			midX: cos * (radius + leaderGap * 0.55),
			midY: sin * (radius + leaderGap * 0.55),
			right,
			idealY: sin * anchorRadius,
			visible: !suppressed.has(index),
		};
	});

	const capacity = Math.max(1, Math.floor((availHalf * 2) / step));

	[true, false].forEach((side) => {
		const stack = positions.map((position, index) => ({ position, index })).filter((entry) => entry.position.right === side);

		// Over capacity: the smallest shares lose their label, and the centre readout still names them.
		if (stack.length > capacity) {
			[...stack]
				.sort((a, b) => arcs[a.index].data.percentage - arcs[b.index].data.percentage)
				.slice(0, stack.length - capacity)
				.forEach((entry) => {
					entry.position.visible = false;
				});
		}

		const kept = stack.filter((entry) => entry.position.visible).sort((a, b) => a.position.idealY - b.position.idealY);

		let cursor = -availHalf + step / 2;
		kept.forEach((entry) => {
			entry.position.y = Math.max(entry.position.idealY, cursor);
			cursor = entry.position.y + step;
		});

		let limit = availHalf - step / 2;
		for (let i = kept.length - 1; i >= 0; i -= 1) {
			kept[i].position.y = Math.min(kept[i].position.y, limit);
			limit = kept[i].position.y - step;
		}
	});

	const stackHalf = positions.reduce((tallest, position) => (position.visible ? Math.max(tallest, Math.abs(position.y) + block / 2) : tallest), 0);

	return { positions, stackHalf };
};

export interface InterventionData {
  code: string;
  description: string;
  panels: string[];
  counter: number;
  snp_count?: number;
  icon: string;
  supplement: string | null;
  is_top_intervention: boolean;
}

interface ChartDataItem {
  intervention: string;
  geneCount: number;
  markerCount: number;
  tag: string;
  percentage: number;
}

interface InterventionsChartProps {
  data: {
    chart_data: (string | number)[][];
    table_data: InterventionData[];
  };
  setSelectedIntervention: React.Dispatch<React.SetStateAction<InterventionData | null>>;
  width?: number;
  height?: number;
}

const InterventionsChart: React.FC<InterventionsChartProps> = ({ data, setSelectedIntervention, width = 600, height = 600 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const pieLayout = useMemo(() => d3.pie<ChartDataItem>().value((d) => d.geneCount).sort(null).padAngle(0.02), []);

  // The width prop is WheelModal's reserve arithmetic, not the column the chart actually gets.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const measure = () => setMeasuredWidth(node.getBoundingClientRect().width || node.clientWidth || 0);
    measure();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Process the chart data
  const processChartData = useCallback(() => {
    if (!data?.chart_data || data.chart_data.length <= 1) return [];

    const processed = data.chart_data.slice(1).map((row, index) => {
      const [intervention, geneCount, tag] = row;
      // chart_data's middle column is a weighted score, so the marker count comes from table_data.
      const detail = data.table_data?.find((item) => item.code === tag);
      return {
        intervention: intervention as string,
        geneCount: geneCount as number,
        markerCount: detail?.snp_count ?? detail?.counter ?? 0,
        tag: tag as string,
        percentage: 0,
      };
    });

    const total = processed.reduce((sum, item) => sum + item.geneCount, 0);
    processed.forEach((item) => {
      item.percentage = total > 0 ? (item.geneCount / total) * 100 : 0;
    });

    return processed;
  }, [data]);

  useEffect(() => {
    const processed = processChartData();
    setChartData(processed);

    // Auto-select first intervention for better UX
    if (processed.length > 0 && data?.table_data && !isInitialized) {
      const firstIntervention = data.table_data.find((item) => item.code === processed[0].tag);
      if (firstIntervention) {
        setSelectedIntervention(firstIntervention);
        setSelectedSegmentIndex(0);
        setIsInitialized(true);
      }
    }
  }, [data, processChartData, isInitialized, setSelectedIntervention]);

  const handleSegmentClick = useCallback(
    (chartItem: ChartDataItem, segmentIndex: number) => {
      if (isAnimating) return;

      const intervention = data?.table_data.find((item) => item.code === chartItem.tag);
      if (intervention) {
        setIsAnimating(true);
        setSelectedIntervention(intervention);
        setSelectedSegmentIndex(segmentIndex);

        // Shorter animation time for modal responsiveness
        setTimeout(() => setIsAnimating(false), 200);
      }
    },
    [data?.table_data, isAnimating, setSelectedIntervention]
  );

  // One layout for the render and for the element box, so the SVG can hug what is actually drawn.
  const layout = useMemo(() => {
    // Only the SVG height is derived from the box, so measuring width alone cannot feed back.
    const boxW = Math.max(MIN_CHART_SIZE, Math.min(measuredWidth || width, MAX_CHART_SIZE));
    const edgePad = boxW * EDGE_PAD;
    const leaderGap = boxW * LEADER_GAP;
    // Everything between the ring and the text, so a label box lands exactly edgePad inside the viewBox.
    const fixedReserve = leaderGap + LABEL_NUDGE + LABEL_TEXT_PAD;
    const baseChars = charsFor(MIN_LABEL_FONT_PX, Math.min(Math.max(boxW * TEXT_ROOM, TEXT_ROOM_MIN), TEXT_ROOM_MAX));
    const affordableChars = charsFor(MIN_LABEL_FONT_PX, boxW / 2 - edgePad - fixedReserve - MIN_RADIUS);
    // Two arcs labelled the same is worse than a smaller ring, so the column widens only on a clash.
    const labelChars = Math.min(distinctLabelChars(chartData, baseChars), Math.max(baseChars, affordableChars));
    // A budget too small to separate two names hides the smaller share rather than printing the label twice.
    const tags = chartData.map((item) => tagFor(item.intervention, labelChars));
    const tagBlock = Math.max(...tags.map((lines) => lines.length), 1) * TAG_LINE_PX + TAG_LINE_PX + TAG_PAD;
    const rendered = tags.map((lines) => lines.join(" "));
    const seen = new Set<string>();
    const ambiguous = new Set<number>();
    chartData
      .map((item, index) => ({ index, share: item.percentage }))
      .sort((a, b) => b.share - a.share)
      .forEach(({ index }) => (seen.has(rendered[index]) ? ambiguous.add(index) : seen.add(rendered[index])));
    const radius = Math.max(MIN_RADIUS, Math.min(boxW / 2 - edgePad - fixedReserve - labelChars * GLYPH_PX, height / 2 - edgePad));
    const arcs = pieLayout(chartData);
    // The column scrolls, so the stack may claim a taller box before any label is dropped.
    const stackBound = boxW * MAX_ASPECT;
    const { positions, stackHalf } = placeLabels(arcs, radius, leaderGap, stackBound / 2 - edgePad, ambiguous, tagBlock, tagBlock + TAG_GAP);
    const halfH = Math.max(radius + leaderGap * 0.3, stackHalf) + edgePad;

    return {
      boxW,
      boxH: Math.min(Math.max(2 * halfH, MIN_CHART_SIZE), Math.max(stackBound, MIN_CHART_SIZE)),
      radius,
      innerRadius: radius * INNER_RATIO,
      labelChars,
      tags,
      tagBlock,
      arcs,
      positions,
    };
  }, [chartData, measuredWidth, width, height, pieLayout]);

  const renderChart = useCallback(() => {
    if (!svgRef.current || !chartData.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { boxW, boxH, radius, innerRadius, tags, tagBlock, arcs, positions } = layout;

    svg.attr("viewBox", `0 0 ${boxW} ${boxH}`);

    const container = svg
      .append("g")
      .attr("transform", `translate(${boxW / 2}, ${boxH / 2})`)
      .style("opacity", 0);

    // Animate container entrance
    container.transition().duration(motionDuration(MOTION_MS.slow)).ease(d3.easeCubicOut).style("opacity", 1);

    const arc = d3.arc<d3.PieArcDatum<ChartDataItem>>().innerRadius(innerRadius).outerRadius(radius).cornerRadius(4);

    const hoverArc = d3
      .arc<d3.PieArcDatum<ChartDataItem>>()
      .innerRadius(innerRadius - 4)
      .outerRadius(radius + 6)
      .cornerRadius(6);

    // Create segments with staggered animation
    const segments = container.selectAll(".segment").data(arcs).enter().append("g").attr("class", "segment").style("cursor", "pointer").style("opacity", 0);

    // Animate segments in
    segments
      .transition()
      .delay((d, i) => motionDuration(i * 20))
      .duration(motionDuration(MOTION_MS.slow))
      .ease(d3.easeCubicOut)
      .style("opacity", 1);

    // Add paths
    const paths = segments
      .append("path")
      .attr("d", (d) => {
        const startArc = d3.arc<d3.PieArcDatum<ChartDataItem>>().innerRadius(innerRadius).outerRadius(innerRadius).cornerRadius(4);
        return startArc(d) || "";
      })
      .attr("class", "interventions-chart__segment")
      .style("fill", (d, i) => fillFor(i))
      .attr("stroke-width", 2);

    // Animate path growth
    paths
      .transition()
      .delay((d, i) => motionDuration(i * 20))
      .duration(motionDuration(MOTION_MS.slow))
      .ease(d3.easeCubicOut)
      .attrTween("d", function (d) {
        const interpolate = d3.interpolate({ startAngle: d.startAngle, endAngle: d.startAngle }, { startAngle: d.startAngle, endAngle: d.endAngle });
        return (t) => {
          const interpolated = interpolate(t);
          return arc({ ...d, ...interpolated }) || "";
        };
      });

    // Apply initial selected state
    if (selectedSegmentIndex !== null && paths.nodes()[selectedSegmentIndex]) {
      const selectedPath = d3.select(paths.nodes()[selectedSegmentIndex]);
      selectedPath.attr("d", (d) => hoverArc(d as d3.PieArcDatum<ChartDataItem>) || "").attr("stroke-width", 3);
    }

    // Create external labels group
    const labelsGroup = container.append("g").attr("class", "external-labels");

    // A segment whose side could not hold another label carries its arc and the centre readout only.
    const labelled = arcs.map((slice, index) => ({ slice, index })).filter((entry) => positions[entry.index].visible);
    const slotOf = new Map<number, number>();
    labelled.forEach((entry, slot) => slotOf.set(entry.index, slot));

    // Add leader lines
    const lines = labelsGroup
      .selectAll(".leader-line")
      .data(labelled)
      .enter()
      .append("polyline")
      .attr("class", "leader-line")
      .attr("fill", "none")
      .style("stroke", (entry) => fillFor(entry.index))
      .attr("stroke-width", 1.5)
      .style("opacity", 0)
      .attr("points", (entry) => {
        const pos = positions[entry.index];
        return `${pos.lineEndX},${pos.lineEndY} ${pos.midX},${pos.midY} ${pos.x > 0 ? pos.x - LABEL_NUDGE : pos.x + LABEL_NUDGE},${pos.y}`;
      });

    // Add label texts first to measure them
    const labelTexts = labelsGroup
      .selectAll(".label-text")
      .data(labelled)
      .enter()
      .append("text")
      .attr("class", "label-text interventions-chart__label")
      .attr("text-anchor", (entry) => positions[entry.index].anchor)
      .style("opacity", 0)
      // Wrapped rather than truncated, so a long name still reads in full inside the reserved column.
      .each(function (entry) {
        const pos = positions[entry.index];
        d3.select(this)
          .selectAll("tspan")
          .data(tags[entry.index])
          .enter()
          .append("tspan")
          .attr("x", pos.x)
          .attr("y", (line, lineIndex) => pos.y - tagBlock / 2 + TAG_LINE_PX * (lineIndex + 1) - 2)
          .text((line) => line);
      });

    // Add label backgrounds after measuring text
    const labelBackgrounds = labelsGroup
      .selectAll(".label-background")
      .data(labelled)
      .enter()
      .append("rect")
      .attr("class", "label-background interventions-chart__label-bg")
      .attr("x", (entry, i) => {
        const pos = positions[entry.index];
        const textWidth = (labelTexts.nodes()[i] as SVGTextElement).getBBox().width;
        return pos.anchor === "start" ? pos.x - LABEL_TEXT_PAD : pos.x - textWidth - LABEL_TEXT_PAD;
      })
      .attr("y", (entry) => positions[entry.index].y - tagBlock / 2)
      .attr("width", (entry, i) => (labelTexts.nodes()[i] as SVGTextElement).getBBox().width + LABEL_TEXT_PAD * 2)
      .attr("height", tagBlock)
      .attr("rx", 4)
      .style("stroke", (entry) => fillFor(entry.index))
      .attr("stroke-width", 1)
      .style("opacity", 0);

    // Add percentage texts
    const percentageTexts = labelsGroup
      .selectAll(".percentage-text")
      .data(labelled)
      .enter()
      .append("text")
      .attr("class", "percentage-text interventions-chart__percentage")
      .attr("x", (entry) => positions[entry.index].x)
      .attr("y", (entry) => positions[entry.index].y - tagBlock / 2 + TAG_LINE_PX * (tags[entry.index].length + 1))
      .attr("text-anchor", (entry) => positions[entry.index].anchor)
      .style("opacity", 0)
      .text((entry) => `${entry.slice.data.percentage.toFixed(1)}%`);

    // Animate external labels
    lines
      .transition()
      .delay((entry, i) => motionDuration(MOTION_MS.slow + i * 20))
      .duration(motionDuration(MOTION_MS.normal))
      .ease(d3.easeCubicOut)
      .style("opacity", LEADER_OPACITY);

    // Three different element types, so the union needs a widened alias before it can be iterated.
    const labelParts: d3.Selection<any, any, any, any>[] = [labelBackgrounds, labelTexts, percentageTexts];

    labelParts.forEach((selection) => {
      selection
        .transition()
        .delay((entry: unknown, i: number) => motionDuration(MOTION_MS.slow + i * 20))
        .duration(motionDuration(MOTION_MS.normal))
        .ease(d3.easeCubicOut)
        .style("opacity", 1);
    });

    // Appended after the ring so the readout is never painted over by a segment.
    const centerGroup = container.append("g").attr("class", "center-text-group");
    // The hole is a circle, so the usable chord at the text's own height is shorter than its diameter.
    const centreChars = charsFor(CENTRE_FONT_PX, innerRadius * 1.6);

    const centerMarkers = centerGroup.append("text").attr("class", "interventions-chart__centre-meta").attr("text-anchor", "middle").attr("dominant-baseline", "middle").style("opacity", 0);
    const centerShare = centerGroup.append("text").attr("class", "interventions-chart__centre-meta").attr("text-anchor", "middle").attr("dominant-baseline", "middle").style("opacity", 0);

    // Function to update center text
    const updateCenterText = (d: d3.PieArcDatum<ChartDataItem> | null) => {
      centerGroup.selectAll("text.interventions-chart__centre-title").remove();

      if (!d) {
        [centerMarkers, centerShare].forEach((selection) => selection.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 0));
        return;
      }

      const titleLines = wrapLabel(d.data.intervention, centreChars, CENTRE_TITLE_LINES);
      const blockHeight = titleLines.length * CENTRE_LINE_PX + CENTRE_META_LINE_PX * 2;
      let cursor = -blockHeight / 2 + CENTRE_LINE_PX / 2;

      titleLines.forEach((line) => {
        centerGroup
          .append("text")
          .attr("class", "interventions-chart__centre-title")
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("y", cursor)
          .style("opacity", 0)
          .text(line)
          .transition()
          .duration(motionDuration(MOTION_MS.normal))
          .style("opacity", 1);
        cursor += CENTRE_LINE_PX;
      });

      centerMarkers.attr("y", cursor).transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1).text(`${d.data.markerCount} markers`);
      centerShare.attr("y", cursor + CENTRE_META_LINE_PX).transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1).text(`${d.data.percentage.toFixed(1)}%`);
    };

    // Initialize center text with selected segment
    if (selectedSegmentIndex !== null && arcs[selectedSegmentIndex]) {
      updateCenterText(arcs[selectedSegmentIndex]);
    }

    // Add interaction handlers
    paths
      .on("mouseover", function (event, d) {
        if (isAnimating) return;

        const currentIndex = arcs.indexOf(d);
        const slot = slotOf.get(currentIndex);
        updateCenterText(d);

        // Clear previous selected state if hovering different segment
        if (selectedSegmentIndex !== null && selectedSegmentIndex !== currentIndex) {
          const prevSelectedPath = d3.select(paths.nodes()[selectedSegmentIndex]);
          prevSelectedPath
            .transition()
            .duration(motionDuration(MOTION_MS.normal))
            .ease(d3.easeCubicOut)
            .attr("d", (prevD) => arc(prevD as d3.PieArcDatum<ChartDataItem>) || "")
            .attr("stroke-width", 2);
        }

        // Apply hover state
        d3.select(this).transition().duration(motionDuration(MOTION_MS.normal)).ease(d3.easeCubicOut).attr("d", hoverArc(d) || "").attr("stroke-width", 3);

        // Highlight corresponding label
        if (slot !== undefined) {
          d3.select(labelBackgrounds.nodes()[slot]).transition().duration(motionDuration(MOTION_MS.normal)).attr("stroke-width", 2);
        }

        // Dim other segments and labels
        segments.filter((_, i) => i !== currentIndex).transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 0.3);

        [lines as d3.Selection<any, any, any, any>, ...labelParts].forEach((selection) => {
          selection
            .filter((_: unknown, i: number) => i !== slot)
            .transition()
            .duration(motionDuration(MOTION_MS.normal))
            .style("opacity", 0.3);
        });
      })
      .on("mouseout", function (event, d) {
        if (isAnimating) return;

        const currentIndex = arcs.indexOf(d);

        // Only revert if this segment is not the selected one
        if (selectedSegmentIndex !== currentIndex) {
          d3.select(this).transition().duration(motionDuration(MOTION_MS.normal)).ease(d3.easeCubicOut).attr("d", arc(d) || "").attr("stroke-width", 2);
        }

        // Restore segments and labels — each group back to its own resting opacity.
        segments.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1);
        lines.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", LEADER_OPACITY);
        labelBackgrounds.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1).attr("stroke-width", 1);
        labelTexts.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1);
        percentageTexts.transition().duration(motionDuration(MOTION_MS.normal)).style("opacity", 1);

        // Show selected segment info in center
        if (selectedSegmentIndex !== null && selectedSegmentIndex !== currentIndex) {
          updateCenterText(arcs[selectedSegmentIndex]);
        } else if (selectedSegmentIndex === null) {
          updateCenterText(null);
        }
      })
      .on("click", (event, d) => {
        if (isAnimating) return;

        handleSegmentClick(d.data, arcs.indexOf(d));
      });
  }, [chartData, layout, handleSegmentClick, isAnimating, selectedSegmentIndex]);

  useLayoutEffect(() => {
    if (chartData.length > 0) {
      renderChart();
    }
  }, [chartData]);

	if (!data?.chart_data || chartData.length === 0) {
		return (
			<div className="interventions-chart" ref={containerRef}>
				<InlineLoading minHeight={Math.min(layout.boxW, MAX_LOADING_HEIGHT)} message="Loading interventions…" />
			</div>
		);
	}

  return (
    <div className="interventions-chart" ref={containerRef}>
      <svg ref={svgRef} className="interventions-chart__svg" width={layout.boxW} height={layout.boxH} viewBox={`0 0 ${layout.boxW} ${layout.boxH}`} preserveAspectRatio="xMidYMid meet" />
    </div>
  );
};

export default InterventionsChart;